import React, { useState, useEffect } from 'react';
import { Breadcumbs, ScreenConstants } from '../BreadCumbs/Breadcrumbs';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { useAtom, useAtomValue } from 'jotai';
import { API } from '@src/api';
import {
  ApiResponse,
  FavoriteFolderPrograms,
  FindhelpProgram,
  GetAllFavoritesResponse,
  MemberInfo,
  GetAllFavoritesResult,
} from '@src/api.types';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { CboCardFindhelp } from '../CboCard/CboCardFindhelp';
import { ReferralFormModal } from '../ReferralForm/ReferralForm';
import { sessionService } from '@src/services/_session_service';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { PaginationComp } from '@repo/ui/src/Pagination/Pagination';
import { CboDetailModal } from '../CboDetailsModal/CboDetailsModal';
import { EmailMember } from '../Email/EmailMember';
import { printFindhelp } from '@src/utills/printProgram';
import { CboReferralSubmissionResults } from '../CboReferralSubmissionResults/CboReferralSubmissionResults';
import { event } from '@uhg-abyss/web/tools/event';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';

export function MyFavorites(): React.JSX.Element {
  const featureFlags = getFeatureFlagsFromStorage();
  const { navigate } = useRouter();
  const memberInContext = useAtomValue(memberInContextAtom);
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const [favoriteFolders, setFavoriteFolders] = useState<
    GetAllFavoritesResult[] | any[]
  >([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [totalcount, setTotalCount] = React.useState(0);
  const [records, setRecordsPerPage] = useState<number>(10);
  const [programResults, setProgramResults] = useState<FindhelpProgram[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [favoritePrograms, setFavoritePrograms] = useState<
    FavoriteFolderPrograms[]
  >([]);

  const [showReferralModal, setShowReferralModal] = useState<boolean>(false);
  const [referringTo, setReferringTo] = useState<FindhelpProgram | null>(null);

  // Print functionality removed in V2 migration.

  const toolTip =
    'The availability of a CBO in a specific zip code can be checked by reviewing the "Coverage Area" on the CBO card or by copying, pasting, or typing the name of the favorited CBO into the CBO search page.';

  const setReferToProgram = (program: FindhelpProgram | null) => {
    if (program !== null) {
      setReferringTo(program);
      setShowReferralModal(true);
    } else {
      setReferringTo(null);
      setShowReferralModal(false);
    }
  };

  const [emailingProgram, setEmailingProgram] = useState<
    FindhelpProgram | undefined
  >(undefined);
  const [referResponse, setReferResponse] = useState<
    ApiResponse<any> | null | undefined
  >(undefined);
  const [currentFolderCount, setCurrentFolderCount] = useState<
    Map<number, number>
  >(new Map());
  const [showDetailsFor, setShowDetailsFor] = useState<
    FindhelpProgram | undefined
  >(undefined);

  async function fetchFavoritePrograms(
    folderId,
    favPrograms: FavoriteFolderPrograms[],
    initalLoad: boolean = false
  ) {
    setIsLoading(true);
    if (initalLoad) {
      setFavoritePrograms(favPrograms);
    }
    const count = favPrograms.length;
    setCurrentFolderId(folderId);
    const programs: FindhelpProgram[] = [];
    const ITEMS_PER_PAGE = 10;
    const indexOfLastItem = pageNumber * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentFavorites = favPrograms.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    const sortedFavorites = sortFavResponses(currentFavorites);
    const responses = await Promise.all(
      sortedFavorites.map((fav) => {
        return API.getFindHelpProgramsUsingId(fav.programId);
      })
    );
    for (const resp of responses) {
      if (!resp?.data) continue;
      resp.data['id'] = resp.data.program_numeric_id;
      programs.push(resp.data);
    }

    currentFolderCount.set(folderId, count);
    setCurrentFolderCount(currentFolderCount);
    setTotalCount(count);
    setProgramResults(programs);
    setIsLoading(false);
  }

  useEffect(() => {
    if (favoritePrograms.length > 0) {
      fetchFavoritePrograms(currentFolderId, favoritePrograms);
    }
  }, [pageNumber]);

  function Pagination({ variant, showPageSize }) {
    if (totalcount === -1 || totalcount === 0) return <div />;
    const pageEvent = (data) => {
      setPageNumber(data);
    };
    const pageSizeEvent = (data) => {
      setRecordsPerPage(data);
    };
    return (
      <PaginationComp
        totalcount={totalcount}
        numberofrecordsPerpags={records}
        handlePageEvent={pageEvent}
        pageSizeEvent={pageSizeEvent}
        variant={variant}
        showPageSize={showPageSize}
        pageNo={pageNumber}
      />
    );
  }

  const sortFavResponses = (favPrograms: FavoriteFolderPrograms[]) => {
    return favPrograms.sort((a, b) => b.id - a.id);
  };

  const deleteProgramFromFavorites = (programId) => async () => {
    if (currentFolderId) {
      const resp = await API.deleteFavorite(currentFolderId, programId);
      if (resp?.status?.toLowerCase() === 'success') {
        const results = programResults.filter(
          (program) => program.program_numeric_id !== programId
        );
        const count = currentFolderCount.get(currentFolderId) - 1;
        currentFolderCount.set(currentFolderId, count);
        setCurrentFolderCount(currentFolderCount);
        setTotalCount(count);
        setProgramResults(results);
        setFavoritePrograms(
          favoritePrograms.filter((fav) => fav.programId !== programId)
        );
        if (results.length === 0) {
          setPageNumber(pageNumber - 1);
        }
      }
    }
  };

  const ProgramCards = () => {
    if (!currentFolderId) {
      return (
        <Text style={{ marginTop: '24px', color: '#666' }}>
          Select a folder on the left to view your saved programs.
        </Text>
      );
    }
    if (totalcount === 0 && !isLoading) {
      return (
        <Text style={{ marginTop: '24px', color: '#666' }}>
          No favorites in this folder.
        </Text>
      );
    }
    if (totalcount > 0 && !isLoading) {
      return (
        <React.Fragment>
          {programResults?.map((program) => {
            return (
              <div key={program.program_numeric_id} style={{ marginBottom: '16px' }}>
                <CboCardFindhelp
                  program={program}
                  isBulkReferring={false}
                  onClickBulkReferToggle={() => {}}
                  onClickRefer={() => setReferToProgram(program)}
                  onClickTitle={() => setShowDetailsFor(program)}
                  onClickService={(service: string) => {}}
                  onClickServingAttribute={(attribute: string) => {}}
                  onClickPrint={() => {
                    printFindhelp(program);
                  }}
                  onClickEmail={() => {
                    event.setData({
                      userId:
                        favoriteFolders.length > 0
                          ? String(favoriteFolders[0].userId)
                          : '',
                    });
                    event('EMAIL_CLICK');
                    setEmailingProgram(program);
                  }}
                  showCheckbox={false}
                  availableFavoritesFolders={[]}
                  isFavorite={true}
                  onSaveFavorite={() => {}}
                  onRemoveFavorite={deleteProgramFromFavorites(
                    program.program_numeric_id
                  )}
                />
              </div>
            );
          })}
        </React.Fragment>
      );
    }
  };

  const fetchFavoriteFolders = async () => {
    const resp: GetAllFavoritesResponse | null = await API.getAllFavorites();
    if (resp?.data && resp?.status === 'success') {
      const folders: GetAllFavoritesResult[] = [];
      resp.data.forEach((folder: GetAllFavoritesResult) => {
        folders.push(folder);
        currentFolderCount.set(folder.id, folder.favoriteFolderPrograms.length);
        setCurrentFolderCount(currentFolderCount);
      });
      setFavoriteFolders(folders);
      if (folders.length > 0 && currentFolderId === null) {
        fetchFavoritePrograms(folders[0].id, folders[0].favoriteFolderPrograms, true);
      }
    }
  };

  useEffect(() => {
    fetchFavoriteFolders();
  }, []);

  // Print functionality has been removed in V2 migration.

  return (
    <React.Fragment>
      <div>
        {isLoading ? (
          <div
            style={{
              zIndex: 999,
              backgroundColor: '#FFFFFF',
              opacity: 0.75,
              position: 'fixed',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              top: 0,
              left: 0,
            }}
          >
            <LoadingSpinner
              size="xl"
              isLoading={isLoading}
              ariaLoadingLabel="Downloading files"
            />
          </div>
        ) : (
          ``
        )}
        {
          // User clicked a program card, show the details modal
          showDetailsFor !== undefined && (
            <CboDetailModal
              vendor="findhelp"
              isOpen={showDetailsFor !== undefined}
              program={showDetailsFor}
              onClose={() => setShowDetailsFor(undefined)}
              isPrinting={false}
            />
          )
        }

        {
          // Show the email modal if the user clicks the email button on a program card
          emailingProgram !== undefined && (
            <EmailMember
              vendor="findhelp"
              programData={emailingProgram}
              isOpen={emailingProgram !== undefined}
              setIsOpen={(next: boolean) => {
                if (!next) setEmailingProgram(undefined);
              }}
            />
          )
        }
        {showReferralModal && referringTo !== undefined && (
          <ReferralFormModal
            vendor="findhelp"
            referringTo={referringTo}
            onClose={() => {
              setReferringTo(undefined);
            }}
            setReferResponse={setReferResponse}
            memberInContext={memberInContext}
            paCoalitionConsent={
              featureFlags?.agent_pennsylvania_coalition_consent
            }
            loggedInAdvocate={loggedInAdvocate}
          />
        )}
        {
          // Submitted a referral, show the user a pop-up on success or failure
          referResponse !== undefined && (
            <CboReferralSubmissionResults
              referResp={referResponse}
              setReferResp={setReferResponse}
            />
          )
        }
      </div>
      <div
        style={{
          backgroundColor: '#EDF8FA',
          paddingRight: '5rem',
          paddingLeft: '5rem',
        }}
      >
        <Grid>
          <Grid.Col span={{ xs: 12, md: 8, lg: 8 }}>
            <Breadcumbs screen={ScreenConstants.MY_FAVORITES} />
            <Layout.Group style={{ marginTop: '18px', marginBottom: '12px' }}>
              <Heading serif size="xl">My Favorites</Heading>
              <Tooltip content={toolTip}>
                <Button size="$sm" aria-label="Tooltip information" icon={{ icon: 'question_mark', position: 'icon-only' }} />
              </Tooltip>
            </Layout.Group>
          </Grid.Col>
        </Grid>
      </div>
      <div
        style={{
          paddingRight: '5rem',
          paddingLeft: '5rem',
          paddingTop: '3rem',
          paddingBottom: '2rem',
        }}
      >
        {favoriteFolders.length === 0 && !isLoading ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <IconSymbol
                  id="arrow_back_ios"
                  icon="arrow_back_ios"
                  size="xs"
                  style={{ color: '#196ECF', cursor: 'pointer' }}
                  onClick={() => {
                    navigate(`/search?zipcode=${memberInContext?.addrZipCode}`);
                  }}
                />
                <Text
                  data-cy="back-to-plan-benefits"
                  style={{ cursor: 'pointer', color: '#196ECF' }}
                  fontWeight="bold"
                  fontSize="lg"
                  onClick={() => {
                    navigate(`/search?zipcode=${memberInContext?.addrZipCode}`);
                  }}
                >
                  Back to CBO search
                </Text>
              </div>
            </div>
            <Text style={{ marginTop: '24px', color: '#666' }}>
              You don't have any saved favorites yet. Browse CBOs and save programs to see them here.
            </Text>
          </>
        ) : (
        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }}>
          <div style={{ flexShrink: 0, width: '220px', minWidth: '160px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px' }}>
                <IconSymbol
                  id="arrow_back_ios"
                  icon="arrow_back_ios"
                  size="xs"
                  style={{ color: '#196ECF', cursor: 'pointer' }}
                  onClick={() => {
                    navigate(`/search?zipcode=${memberInContext?.addrZipCode}`);
                  }}
                />
                <Text
                  data-cy="back-to-plan-benefits"
                  style={{ cursor: 'pointer', color: '#196ECF' }}
                  fontWeight="bold"
                  fontSize="lg"
                  onClick={() => {
                    navigate(`/search?zipcode=${memberInContext?.addrZipCode}`);
                  }}
                >
                  Back to CBO search
                </Text>
              </div>
              {favoriteFolders?.map((folder: GetAllFavoritesResult, index) => {
                return (
                  <React.Fragment>
                    <div
                      data-cy="favorite-folder"
                      key={`folder-${index}`}
                      style={{
                        width: '100%',
                      }}
                    >
                      <Flex
                        alignItems="center"
                        style={{ width: '100%' }}
                        id={`fav-card-${index}`}
                      >
                        <IconSymbol
                          icon="folder"
                          size="35px"
                          color="$primary1"
                          variant="outlined"
                          style={{
                            paddingRight: '5px',
                            marginRight: '10px',
                            cursor: 'pointer',
                          }}
                        />
                        <Text
                          style={{
                            cursor: 'pointer',
                          }}
                          fontSize="lg"
                          fontWeight={
                            currentFolderId === folder.id ? 'bold' : 'regular'
                          }
                          color="$primary1"
                          onClick={() => {
                            return fetchFavoritePrograms(
                              folder.id,
                              folder.favoriteFolderPrograms,
                              true
                            );
                          }}
                        >
                          {folder.folderName} (
                          {currentFolderCount.get(folder.id)})
                        </Text>
                      </Flex>
                    </div>
                    <div style={{ padding: '8px 0px 8px 0px', width: '100%' }}>
                      <Divider />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ paddingBottom: '8px' }}>
              <Pagination variant="compact" showPageSize="true" />
            </div>
            <ProgramCards />
          </div>
        </div>
        )}
      </div>
    </React.Fragment>
  );
}
